import {
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  CallDto,
  CallerDto,
  CallerSnipeDto,
  CallPeriod,
  CallSourceStatusDto,
  CursorPage,
  PostCallInput,
  SnipeDto,
  TelegramSettingsDto,
  UpdateCallerSnipeInput,
  UpdateTelegramSettingsInput,
} from '@ultrasnip/contracts';
import { api } from '@/lib/api-client';

export const CallKeys = {
  all: ['calls'] as const,
  list: (filters: CallFilters) => ['calls', 'list', filters] as const,
  callers: ['calls', 'callers'] as const,
  status: ['calls', 'status'] as const,
  snipeCallers: ['snipes', 'callers'] as const,
  snipes: ['snipes', 'recent'] as const,
  telegramSettings: ['admin', 'telegram'] as const,
};

export interface CallFilters {
  callerId: string | undefined;
  period: CallPeriod;
  q: string;
}

const PAGE = 100;
/** New calls arrive at any time; the lists catch up this often. */
const REFRESH_MS = 15_000;

export function callsQuery(filters: CallFilters) {
  return infiniteQueryOptions({
    queryKey: CallKeys.list(filters),
    queryFn: ({ signal, pageParam }) =>
      api.get<CursorPage<CallDto>>('/calls', {
        query: {
          limit: PAGE,
          callerId: filters.callerId,
          period: filters.period,
          q: filters.q || undefined,
          cursor: pageParam ?? undefined,
        },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // A new filter keeps the current rows on screen until its results arrive.
    placeholderData: keepPreviousData,
    // A snapshot is being taken while a call shows "—": look again soon.
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) => page.items.some((call) => call.snapshot === null))
        ? 3_000
        : REFRESH_MS,
  });
}

export const callersQuery = queryOptions({
  queryKey: CallKeys.callers,
  queryFn: ({ signal }) => api.get<CallerDto[]>('/calls/callers', { signal }),
  staleTime: 10_000,
  refetchInterval: REFRESH_MS,
});

export const callSourceQuery = queryOptions({
  queryKey: CallKeys.status,
  queryFn: ({ signal }) => api.get<CallSourceStatusDto>('/calls/status', { signal }),
  // While the bot is connecting (or was just changed), its state settles within seconds.
  refetchInterval: (query) => (query.state.data?.telegram.state === 'starting' ? 2_000 : REFRESH_MS),
});

/** Admins: the bot's settings (the token itself never comes back, only whether it is set). */
export const telegramSettingsQuery = queryOptions({
  queryKey: CallKeys.telegramSettings,
  queryFn: ({ signal }) => api.get<TelegramSettingsDto>('/admin/telegram', { signal }),
});

export function useUpdateTelegramSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTelegramSettingsInput) =>
      api.put<TelegramSettingsDto>('/admin/telegram', input),
    onSuccess: (saved) => {
      client.setQueryData(CallKeys.telegramSettings, saved);
      void client.invalidateQueries({ queryKey: CallKeys.status });
    },
  });
}

export const snipeCallersQuery = queryOptions({
  queryKey: CallKeys.snipeCallers,
  queryFn: ({ signal }) => api.get<CallerSnipeDto[]>('/snipes/callers', { signal }),
  refetchInterval: REFRESH_MS,
});

export const snipesQuery = queryOptions({
  queryKey: CallKeys.snipes,
  queryFn: ({ signal }) => api.get<SnipeDto[]>('/snipes', { signal }),
  // While the engine is on a snipe, its outcome lands within seconds.
  refetchInterval: (query) =>
    query.state.data?.some((snipe) => snipe.status === 'pending') ? 2_000 : REFRESH_MS,
});

/** Saves one caller's snipe; the row shows the change at once and rolls back if it is refused. */
export function useUpdateCallerSnipe() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ callerId, input }: { callerId: string; input: UpdateCallerSnipeInput }) =>
      api.patch<CallerSnipeDto>(`/snipes/callers/${callerId}`, input),
    onMutate: async ({ callerId, input }) => {
      await client.cancelQueries({ queryKey: CallKeys.snipeCallers });
      const before = client.getQueryData<CallerSnipeDto[]>(CallKeys.snipeCallers);
      client.setQueryData<CallerSnipeDto[]>(CallKeys.snipeCallers, (rows) =>
        rows?.map((row) => (row.callerId === callerId ? { ...row, ...input } : row)),
      );
      return { before };
    },
    onError: (_error, _vars, context) => {
      if (context?.before) client.setQueryData(CallKeys.snipeCallers, context.before);
    },
    onSuccess: (saved) => {
      client.setQueryData<CallerSnipeDto[]>(CallKeys.snipeCallers, (rows) =>
        rows?.map((row) => (row.callerId === saved.callerId ? saved : row)),
      );
    },
  });
}

/** Admins: post a call by hand. */
export function usePostCall() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: PostCallInput) => api.post<CallDto>('/calls', input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: CallKeys.all });
      void client.invalidateQueries({ queryKey: ['snipes'] });
    },
  });
}
